import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useHomeNews } from "../../hooks/useHomeNews";
import { dictionary, Language } from "../../utils/dictionary";
import NewsBanner from "./NewsBanner";

interface NewsPanelProps {
  language: Language;
}

export default function NewsPanel({ language }: NewsPanelProps) {
  const { slides, autoRotateMs } = useHomeNews();
  const [activeIndex, setActiveIndex] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      setActiveIndex(((index % slides.length) + slides.length) % slides.length);
    },
    [slides.length]
  );

  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  // Auto-rotacion
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, autoRotateMs);
    return () => clearInterval(timer);
  }, [activeIndex, slides.length, autoRotateMs]);

  return (
    <div
      className="flex flex-col rounded-2xl overflow-hidden"
      style={{
        width: "clamp(320px, 24vw, 420px)",
        background: "hsl(0 0% 100% / 0.45)",
        border: "1px solid hsl(0 0% 100% / 0.5)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        boxShadow:
          "0 8px 32px hsl(226 76% 52% / 0.06), inset 0 1px 0 hsl(0 0% 100% / 0.4)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="text-[11px] font-bold tracking-[0.15em] uppercase text-[hsl(230,10%,46%)]">
            {dictionary["updates"]?.[language] ?? "ACTUALIZACIONES"}
          </span>
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums leading-none shadow-[0_0_8px_rgba(239,68,68,0.5)] animate-pulse">
            {slides.length} {slides.length === 1 ? (dictionary["new-singular"]?.[language] ?? "nueva") : (dictionary["new-plural"]?.[language] ?? "nuevas")}
          </span>
        </div>

        <div className="flex gap-1">
          <button
            onClick={prev}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors hover:bg-[hsl(230,25%,93%,0.6)]"
          >
            <ChevronLeft className="w-4 h-4 text-[hsl(230,10%,46%)]" />
          </button>
          <button
            onClick={next}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors hover:bg-[hsl(230,25%,93%,0.6)]"
          >
            <ChevronRight className="w-4 h-4 text-[hsl(230,10%,46%)]" />
          </button>
        </div>
      </div>

      {/* Carrusel */}
      <NewsBanner
        slides={slides}
        activeIndex={activeIndex}
        onDotClick={goTo}
      />

      {/* Lista de noticias */}
      <div className="px-2 pt-2 pb-3">
        {slides.map((slide, i) => {
          const Icon = slide.listIcon;
          const isActive = i === activeIndex;

          return (
            <button
              key={slide.id}
              onClick={() => goTo(i)}
              className={`w-full flex items-center gap-4 px-3 py-3 rounded-xl text-left transition-colors ${
                isActive
                  ? "bg-[hsl(230,25%,93%,0.7)]"
                  : "hover:bg-[hsl(230,25%,93%,0.5)]"
              }`}
            >
              <div
                className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center transition-colors ${
                  isActive
                    ? "bg-[hsl(0,0%,98%,0.6)]"
                    : "bg-[hsl(230,25%,93%)]"
                }`}
              >
                <Icon className="w-[18px] h-[18px] text-[hsl(230,10%,46%)]" />
              </div>
              <span className="text-[13px] font-medium leading-snug line-clamp-2 text-[hsl(230,25%,12%)]">
                {slide.listTitle}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
